import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import QrCode from '@/components/ui/QrCode';
import '../loyalty.css';
import { formatExpiry, isolate, rewardValueLabel } from '../format';
import type { LoyaltyWalletCoupon } from '../loyaltyApi';
import CopyableCode from './CopyableCode';

interface Props {
  coupons: LoyaltyWalletCoupon[];
  /** Restaurant timezone (IANA) — expiry is read on the restaurant's calendar day. */
  tz: string;
  /** The coupon whose QR + code are open. */
  openCode: string | null;
  onToggle: (code: string) => void;
  /** Tap on an opened coupon's QR: the page shows it full screen. */
  onExpandQr: (coupon: LoyaltyWalletCoupon) => void;
  /** Just exchanged: the list scrolls to it and says so. */
  unlocked: { code: string; name: string } | null;
}

/**
 * « Mes avantages » — the permanent wallet of exchanged rewards (SC-1091). It
 * replaces the throwaway modal that showed a code once and lost it on close.
 */
export default function WalletList({ coupons, tz, openCode, onToggle, onExpandQr, unlocked }: Props) {
  const { t, i18n } = useTranslation();
  const unlockedRef = useRef<HTMLLIElement | null>(null);

  // The coupon shows up when the card refetches, a moment after the exchange —
  // so this reacts to the list growing, not only to `unlocked` changing.
  useEffect(() => {
    if (!unlocked) return;
    const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    unlockedRef.current?.scrollIntoView?.({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
  }, [unlocked, coupons.length]);

  return (
    <section data-testid="loyalty-wallet" aria-labelledby="loyalty-wallet-title">
      <h3 id="loyalty-wallet-title" className="m-0 mb-3 font-display text-lg text-ink">
        {t('loyalty.wallet.title')}
      </h3>

      {/* Live region kept in the DOM so the announcement is read when it fills. */}
      <div role="status">
        {unlocked && (
          <p className="m-0 mb-3 rounded-btn-sm border-2 border-solid border-ink bg-mint px-3 py-2 text-sm text-[var(--on-primary)]">
            <strong>✓ {t('loyalty.successTitle')}</strong> {t('loyalty.wallet.unlocked', { name: isolate(unlocked.name, i18n.language) })}
          </p>
        )}
      </div>

      {coupons.length === 0 ? (
        <p className="m-0 rounded-btn border-2 border-dashed border-[var(--ink-faint)] p-5 text-center text-sm text-ink-mute">
          {t('loyalty.wallet.empty')}
        </p>
      ) : (
        <ul className="m-0 list-none space-y-3 p-0">
          {coupons.map((coupon) => {
            const open = openCode === coupon.code;
            const valueLabel = rewardValueLabel(t, { type: coupon.reward_type, value: coupon.value });
            const panelId = `loyalty-coupon-${coupon.code}`;
            return (
              <li
                key={coupon.code}
                ref={unlocked?.code === coupon.code ? unlockedRef : undefined}
                className="overflow-hidden rounded-btn border-2 border-solid border-ink bg-paper shadow-stamp-sm"
              >
                <button
                  type="button"
                  aria-expanded={open}
                  aria-controls={panelId}
                  onClick={() => onToggle(coupon.code)}
                  className="flex min-h-[64px] w-full items-stretch border-0 bg-transparent p-0 text-start text-ink"
                >
                  <span className="flex w-24 flex-none items-center justify-center border-0 border-e-2 border-dashed border-ink bg-mint px-2 text-center font-display text-lg leading-tight text-[var(--on-primary)]">
                    {valueLabel ? <bdi dir="ltr">{valueLabel}</bdi> : '🎁'}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col justify-center gap-1 px-3 py-2">
                    <span dir="auto" className="break-words font-display text-base leading-tight text-ink">
                      {coupon.reward_name}
                    </span>
                    {coupon.expires_at && (
                      <span className="font-mono text-xs text-ink-mute">
                        {t('loyalty.validUntil', { date: formatExpiry(coupon.expires_at, tz, i18n.language) })}
                      </span>
                    )}
                    {/* The chevron sits with the badge, on the start side of the row:
                        the floating « haut de page » button covers the far edge. */}
                    <span className="flex items-center gap-2">
                      <span
                        className={`w-fit rounded-btn-sm border-2 border-solid border-ink px-2 py-[1px] font-mono text-[11px] font-bold ${
                          coupon.usable_online ? 'bg-mint text-[var(--on-primary)]' : 'bg-paper text-ink'
                        }`}
                      >
                        {coupon.usable_online ? t('loyalty.wallet.usableOnline') : t('loyalty.wallet.counterOnly')}
                      </span>
                      <span aria-hidden="true" className="text-lg leading-none text-ink">
                        {open ? '▴' : '▾'}
                      </span>
                    </span>
                  </span>
                </button>
                {open && (
                  <div id={panelId} className="flex flex-col items-center border-0 border-t-2 border-solid border-ink px-4 py-4 text-center">
                    <button
                      type="button"
                      onClick={() => onExpandQr(coupon)}
                      aria-label={t('loyalty.card.qrExpand')}
                      className="rounded-btn-sm border-2 border-solid border-ink bg-white p-2"
                    >
                      <QrCode
                        payload={coupon.qr_payload}
                        size={264}
                        label={t('loyalty.wallet.qrLabel', { name: coupon.reward_name })}
                        className="block h-auto w-[264px] max-w-full"
                      />
                    </button>
                    <p className="m-0 mt-2 font-mono text-xs text-ink-mute">{t('loyalty.card.qrTap')}</p>
                    <CopyableCode code={coupon.code} codeClassName="text-xl text-ink" className="mt-3" />
                    <p className="m-0 mt-1 max-w-[32ch] text-sm text-ink-mute">
                      {coupon.usable_online ? t('loyalty.wallet.couponHint') : t('loyalty.wallet.couponHintCounter')}
                    </p>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
